import { FC, MouseEventHandler, PropsWithChildren } from 'react';
import { MultilinkStoryblok } from 'storyblok-types';
import { Link } from 'atoms/Link/Link';

const cleanDoubleSlashes = (url: string) => {
  const reduceSlashesRegEx = /\/{2,}/g;
  return url.replace(reduceSlashesRegEx, '/');
};

const withoutTrailingSlash = (url: string) => {
  const trailingSlash = /\/+$/g;
  return url.replace(trailingSlash, '');
};

const makeAbsoluteUrl = (url: string) => cleanDoubleSlashes(`/${url}`);

const withAnchor = (url: string, anchor?: string) =>
  anchor ? `${url}#${anchor}` : url;

// Generic function to generate a href from any storyblok link
// https://www.storyblok.com/docs/guide/in-depth/rendering-the-link-field
export const renderLink = (link?: MultilinkStoryblok): string => {
  if (!link) {
    return '';
  }

  if (link.linktype === 'asset') {
    return link.url || link.href;
  }

  if (link.linktype === 'email') {
    return `mailto:${link.email}` || link.href;
  }

  if (link.linktype === 'url' && (link.url || link.href)) {
    const url = link.url || link.href;
    return withAnchor(url, link.anchor);
  }

  const storyLink =
    link.linktype === 'story' && (link?.story?.full_slug || link?.cached_url);

  if (link.linktype === 'story' && storyLink) {
    let url = makeAbsoluteUrl(storyLink);

    if (url === '/home') {
      url = '/';
    }

    url = withoutTrailingSlash(url);
    url = withAnchor(url, link.anchor);

    return url;
  }

  if (link.href) {
    return withAnchor(link.href, link.anchor);
  }

  return '';
};

interface StoryBlokLinkProps {
  link: MultilinkStoryblok;
  target?: string;
  className?: string;
  onClick?: MouseEventHandler;
}

export const StoryBlokLink: FC<StoryBlokLinkProps & PropsWithChildren> = (
  props,
) => {
  return (
    <Link {...props} href={renderLink(props.link)}>
      {props.children}
    </Link>
  );
};
